import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Toaster } from '../components/ui/toaster';
import { Cookie, CheckCircle, Settings, Info, Mail } from 'lucide-react';

const CookiePolicy = () => {
  return (
    <div className="min-h-screen bg-white">
      <Header />
      <Toaster />

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6 bg-gradient-to-br from-orange-50 via-white to-red-50">
        <div className="max-w-7xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-orange-100 to-red-100 text-orange-700 px-5 py-2.5 rounded-full text-sm font-bold mb-8">
            <Cookie className="w-4 h-4" />
            Cookie Policy
          </div>
          <h1 className="text-5xl lg:text-7xl font-black mb-8">
            Cookie
            <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-red-600">
              Policy
            </span>
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Last Updated: January 2025
          </p>
        </div>
      </section>

      {/* Policy Content */}
      <section className="py-24 px-6">
        <div className="max-w-4xl mx-auto">
          
          {/* Introduction */}
          <div className="mb-12 p-8 bg-gradient-to-br from-orange-50 to-red-50 rounded-2xl border-2 border-orange-100">
            <h2 className="text-3xl font-black mb-6">Introduction</h2>
            <p className="text-lg text-gray-700 leading-relaxed">
              This Cookie Policy explains how AALIGN ("we", "us", or "our") uses cookies and similar tracking technologies when you access or use our platform. By using our services, you consent to the use of cookies as described in this policy.
            </p>
          </div>

          {/* 1. What Are Cookies */}
          <div className="mb-12">
            <h2 className="text-3xl font-black mb-6 flex items-center gap-3">
              <Info className="w-8 h-8 text-blue-600" />
              1. What Are Cookies?
            </h2>
            <p className="text-lg text-gray-700 leading-relaxed mb-4">
              Cookies are small text files that are placed on your device (computer, smartphone, or tablet) when you visit a website. They are widely used to make websites work more efficiently and provide information to website owners.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed">
              Cookies can be "session cookies" (temporary and deleted when you close your browser) or "persistent cookies" (remaining on your device for a set period or until manually deleted).
            </p>
          </div>

          {/* 2. Types of Cookies We Use */}
          <div className="mb-12">
            <h2 className="text-3xl font-black mb-6 flex items-center gap-3">
              <Cookie className="w-8 h-8 text-amber-600" />
              2. Types of Cookies We Use
            </h2>
            <div className="space-y-6">
              
              <div className="p-6 bg-blue-50 rounded-xl border-2 border-blue-100">
                <h3 className="text-2xl font-bold text-gray-900 mb-3">2.1. Essential Cookies</h3>
                <p className="text-lg text-gray-700 leading-relaxed mb-3">
                  These cookies are necessary for the platform to function properly. They enable core features such as:
                </p>
                <ul className="space-y-2 ml-6">
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-blue-600 rounded-full mt-3 flex-shrink-0"></div>
                    <span className="text-lg text-gray-700">User authentication and session management</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-blue-600 rounded-full mt-3 flex-shrink-0"></div>
                    <span className="text-lg text-gray-700">Security and fraud prevention</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-blue-600 rounded-full mt-3 flex-shrink-0"></div>
                    <span className="text-lg text-gray-700">Load balancing and system stability</span>
                  </li>
                </ul>
                <p className="text-lg text-gray-700 leading-relaxed mt-3 font-semibold">
                  Without these cookies, the platform cannot function properly.
                </p>
              </div>

              <div className="p-6 bg-purple-50 rounded-xl border-2 border-purple-100">
                <h3 className="text-2xl font-bold text-gray-900 mb-3">2.2. Performance Cookies</h3>
                <p className="text-lg text-gray-700 leading-relaxed mb-3">
                  These cookies collect information about how you use our platform, such as:
                </p>
                <ul className="space-y-2 ml-6">
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-purple-600 rounded-full mt-3 flex-shrink-0"></div>
                    <span className="text-lg text-gray-700">Pages visited and time spent on each page</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-purple-600 rounded-full mt-3 flex-shrink-0"></div>
                    <span className="text-lg text-gray-700">Error messages encountered</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-purple-600 rounded-full mt-3 flex-shrink-0"></div>
                    <span className="text-lg text-gray-700">Platform performance metrics</span>
                  </li>
                </ul>
                <p className="text-lg text-gray-700 leading-relaxed mt-3">
                  These cookies help us improve the user experience and optimize platform performance.
                </p>
              </div>

              <div className="p-6 bg-green-50 rounded-xl border-2 border-green-100">
                <h3 className="text-2xl font-bold text-gray-900 mb-3">2.3. Functional Cookies</h3>
                <p className="text-lg text-gray-700 leading-relaxed mb-3">
                  These cookies allow the platform to remember your preferences and provide enhanced features:
                </p>
                <ul className="space-y-2 ml-6">
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-green-600 rounded-full mt-3 flex-shrink-0"></div>
                    <span className="text-lg text-gray-700">Language preferences</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-green-600 rounded-full mt-3 flex-shrink-0"></div>
                    <span className="text-lg text-gray-700">Display settings and customizations</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-green-600 rounded-full mt-3 flex-shrink-0"></div>
                    <span className="text-lg text-gray-700">User interface preferences</span>
                  </li>
                </ul>
              </div>

              <div className="p-6 bg-orange-50 rounded-xl border-2 border-orange-100">
                <h3 className="text-2xl font-bold text-gray-900 mb-3">2.4. Analytics Cookies</h3>
                <p className="text-lg text-gray-700 leading-relaxed mb-3">
                  We use analytics cookies to understand how users interact with our platform:
                </p>
                <ul className="space-y-2 ml-6">
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-orange-600 rounded-full mt-3 flex-shrink-0"></div>
                    <span className="text-lg text-gray-700">User behavior and navigation patterns</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-orange-600 rounded-full mt-3 flex-shrink-0"></div>
                    <span className="text-lg text-gray-700">Feature usage statistics</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-orange-600 rounded-full mt-3 flex-shrink-0"></div>
                    <span className="text-lg text-gray-700">Traffic sources and conversion tracking</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* 3. Third-Party Cookies */}
          <div className="mb-12 p-8 bg-white rounded-2xl border-2 border-gray-200">
            <h2 className="text-3xl font-black mb-6">3. Third-Party Cookies</h2>
            <p className="text-lg text-gray-700 leading-relaxed mb-4">
              In addition to our own cookies, we may use third-party cookies from trusted service providers for:
            </p>
            <ul className="space-y-3 ml-6">
              <li className="flex items-start gap-3">
                <CheckCircle className="w-6 h-6 text-green-600 mt-1 flex-shrink-0" />
                <div>
                  <span className="font-bold text-gray-900">Analytics and Monitoring:</span>
                  <span className="text-lg text-gray-700"> Google Analytics, Mixpanel, or similar services to analyze usage patterns.</span>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="w-6 h-6 text-green-600 mt-1 flex-shrink-0" />
                <div>
                  <span className="font-bold text-gray-900">Payment Processing:</span>
                  <span className="text-lg text-gray-700"> Payment gateways may set cookies to facilitate secure transactions.</span>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="w-6 h-6 text-green-600 mt-1 flex-shrink-0" />
                <div>
                  <span className="font-bold text-gray-900">Customer Support:</span>
                  <span className="text-lg text-gray-700"> Live chat and support tools may use cookies for session management.</span>
                </div>
              </li>
            </ul>
            <p className="text-lg text-gray-700 leading-relaxed mt-4">
              These third parties have their own privacy policies, and we encourage you to review them.
            </p>
          </div>

          {/* 4. How to Manage Cookies */}
          <div className="mb-12">
            <h2 className="text-3xl font-black mb-6 flex items-center gap-3">
              <Settings className="w-8 h-8 text-indigo-600" />
              4. How to Manage Cookies
            </h2>
            <div className="space-y-6">
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">4.1. Browser Settings</h3>
                <p className="text-lg text-gray-700 leading-relaxed mb-3">
                  Most web browsers allow you to control cookies through their settings. You can:
                </p>
                <ul className="space-y-2 ml-6">
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-indigo-600 rounded-full mt-3 flex-shrink-0"></div>
                    <span className="text-lg text-gray-700">Block all cookies</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-indigo-600 rounded-full mt-3 flex-shrink-0"></div>
                    <span className="text-lg text-gray-700">Accept only first-party cookies</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-indigo-600 rounded-full mt-3 flex-shrink-0"></div>
                    <span className="text-lg text-gray-700">Delete cookies after each browsing session</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-indigo-600 rounded-full mt-3 flex-shrink-0"></div>
                    <span className="text-lg text-gray-700">Clear all existing cookies</span>
                  </li>
                </ul>
              </div>

              <div className="p-6 bg-yellow-50 rounded-xl border-2 border-yellow-200">
                <h3 className="text-2xl font-bold text-gray-900 mb-3">4.2. Impact of Disabling Cookies</h3>
                <p className="text-lg text-gray-700 leading-relaxed">
                  <strong className="text-yellow-800">Important:</strong> Disabling cookies may impact the functionality of the platform. Some features may not work correctly, and you may not be able to log in or access certain services.
                </p>
              </div>

              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">4.3. Browser-Specific Instructions</h3>
                <p className="text-lg text-gray-700 leading-relaxed mb-3">
                  For detailed instructions on managing cookies, please refer to your browser's help documentation:
                </p>
                <ul className="space-y-2 ml-6">
                  <li className="text-lg text-gray-700">
                    <strong>Chrome:</strong> Settings → Privacy and security → Cookies and other site data
                  </li>
                  <li className="text-lg text-gray-700">
                    <strong>Firefox:</strong> Settings → Privacy & Security → Cookies and Site Data
                  </li>
                  <li className="text-lg text-gray-700">
                    <strong>Safari:</strong> Preferences → Privacy → Cookies and website data
                  </li>
                  <li className="text-lg text-gray-700">
                    <strong>Edge:</strong> Settings → Cookies and site permissions → Manage and delete cookies
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* 5. Cookie Retention */}
          <div className="mb-12 p-8 bg-white rounded-2xl border-2 border-gray-200">
            <h2 className="text-3xl font-black mb-6">5. Cookie Retention</h2>
            <p className="text-lg text-gray-700 leading-relaxed mb-4">
              The retention period for cookies varies depending on their type:
            </p>
            <ul className="space-y-3 ml-6">
              <li className="flex items-start gap-3">
                <div className="font-bold text-gray-900 min-w-[140px]">Session Cookies:</div>
                <span className="text-lg text-gray-700">Deleted when you close your browser</span>
              </li>
              <li className="flex items-start gap-3">
                <div className="font-bold text-gray-900 min-w-[140px]">Persistent Cookies:</div>
                <span className="text-lg text-gray-700">Remain for a specified period (typically 30 days to 2 years)</span>
              </li>
            </ul>
          </div>

          {/* 6. Updates to This Policy */}
          <div className="mb-12">
            <h2 className="text-3xl font-black mb-6">6. Updates to This Cookie Policy</h2>
            <p className="text-lg text-gray-700 leading-relaxed">
              We may update this Cookie Policy from time to time to reflect changes in our practices or legal requirements. Any updates will be posted on this page with a revised "Last Updated" date. We encourage you to review this policy periodically.
            </p>
          </div>

          {/* 7. Contact Us */}
          <div className="mb-12 p-8 bg-gradient-to-r from-orange-500 to-red-600 rounded-2xl text-white">
            <h2 className="text-3xl font-black mb-6 flex items-center gap-3">
              <Mail className="w-8 h-8" />
              7. Contact Us
            </h2>
            <p className="text-lg leading-relaxed mb-6">
              If you have any questions about our use of cookies or this Cookie Policy, please contact us:
            </p>
            <div className="space-y-3">
              <p className="text-lg">
                <strong>Email:</strong>{' '}
                <a href="mailto:support@aalign.io" className="text-orange-100 hover:text-white font-semibold underline">
                  support@aalign.io
                </a>
              </p>
              <p className="text-lg">
                <strong>Website:</strong>{' '}
                <a href="https://www.aalign.io" className="text-orange-100 hover:text-white font-semibold underline">
                  www.aalign.io
                </a>
              </p>
            </div>
          </div>

          {/* Contact CTA */}
          <div className="mt-16 p-8 bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl text-center border-2 border-gray-200">
            <h3 className="text-2xl font-bold mb-4 text-gray-900">Need Help Managing Your Cookie Preferences?</h3>
            <p className="text-lg mb-6 text-gray-600">
              Our support team can guide you through cookie management options.
            </p>
            <a
              href="mailto:support@aalign.io"
              className="inline-block bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white font-bold px-8 py-3 rounded-xl shadow-lg transition-all transform hover:scale-105"
            >
              Contact Support
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CookiePolicy;
